import React, { useEffect, useState } from 'react';

import { Loader, Select, SelectProps, useMantineTheme } from '@mantine/core';
import { SelectItem } from '@mantine/core/lib/Select/types';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompletePackage from 'hooks/package/useAutoCompletePackage';

interface IPackageSelectProps extends Omit<SelectProps, 'data' | 'rightSection'> {
  value: string;
  packageId?: string;
  excludeCreditBased?: boolean;
}

const PackageSelect = ({
  value,
  packageId,
  excludeCreditBased,
  ...selectProps
}: IPackageSelectProps): JSX.Element => {
  const theme = useMantineTheme();
  const [options, setOptions] = useState<SelectItem[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 250);
  const { data, isSuccess, isLoading } = useAutoCompletePackage({
    search: debouncedSearchTerm,
    types: excludeCreditBased ? ['TIME_BASED', 'TIME_OR_CREDIT_BASED'] : undefined,
  });

  useEffect(() => {
    if (isSuccess && data) {
      const packages = data.data.results
        .filter(packageInstance => packageInstance.packageId !== packageId)
        .map(packageInstance => ({
          ...packageInstance,
          value: packageInstance.packageId,
          label: packageInstance.name,
        }));
      setOptions(packages);
    }
  }, [data, isSuccess, packageId]);

  return (
    <Select
      {...selectProps}
      data={options}
      searchable
      onSearchChange={setSearchTerm}
      nothingFound='Packages Not Found'
      maxDropdownHeight={100}
      rightSection={isLoading ? <Loader size={theme.fontSizes.md} /> : undefined}
      clearable
      value={value}
    />
  );
};

export default PackageSelect;
